import type { SceneAction, Vec3, SpatialState, SpatialEntity, AlertLevel, EncounterState } from "../types/game";
import { triggerShake } from "./shakeState";
import { soundSystem } from "./SoundSystem";
import { triggerWarp } from "./warpState";
import { IMPULSE_SPEEDS, SCENE } from "./config";
import type { MissionLogger } from "./MissionLogger";

// ── Active effect type for rendering in Viewport3D ──
export interface ActiveEffect {
  id: string;
  type: "beam" | "torpedo" | "shield_impact" | "explosion" | "scan" | "tractor" | "transporter" | "cloak";
  from?: Vec3;
  to?: Vec3;
  position?: Vec3;
  color?: string;
  duration: number;
  startedAt: number; // elapsed time when effect was created
  size?: number;
}

// ── Movement types ──
interface MoveTo {
  kind: "move_to";
  entityId: string;
  from: Vec3;
  to: Vec3;
  duration: number;
  elapsed: number;
  headingFrom?: Vec3;
  headingTo?: Vec3;
  orbitOnArrival?: string; // entity ID to orbit when move completes
}

interface Rotate {
  kind: "rotate";
  entityId: string;
  from: Vec3;
  to: Vec3;
  duration: number;
  elapsed: number;
}

interface Orbit {
  kind: "orbit";
  entityId: string;
  targetId: string;
  radius: number;
  angle: number; // current angle in radians
  speed: number; // radians per second
  elapsed: number;
  headingFrom?: Vec3; // heading at orbit entry, for smooth transition
}

type ActiveMovement = MoveTo | Rotate | Orbit;

// ── Queued command ──
interface QueuedCommand {
  command: SceneAction;
  fireAt: number;
}

const WEAPON_COLORS: Record<string, string> = {
  phaser: "#4488ff",
  disruptor: "#44ff44",
  cutting_beam: "#ff4444",
  photon: "#ff6600",
  quantum: "#4466ff",
  plasma: "#44ff88",
};

const SPEED_VALUES = IMPULSE_SPEEDS;

function lerpVec3(a: Vec3, b: Vec3, t: number): Vec3 {
  const v: Vec3 = {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
  // Normalize to keep it a unit direction vector
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1;
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

export class SceneCommandExecutor {
  private spatialState: SpatialState;
  private queue: QueuedCommand[] = [];
  private movements: ActiveMovement[] = [];
  private _effects: ActiveEffect[] = [];
  private elapsed = 0;
  private effectIdCounter = 0;
  private alertLevelCallback: ((level: AlertLevel) => void) | null = null;
  private onStopCallback: ((entityId: string) => void) | null = null;
  // Fired when an incoming weapon (beam or torpedo) targets player_ship.
  // The hook collapses overlays/hails so combat is always visible.
  private onPlayerHitCallback: ((entityId: string) => void) | null = null;
  private playerShipName = "Player Ship";
  private playerShipFaction = "Unknown";
  private _logger: MissionLogger | null = null;
  private _encounterState: EncounterState | null = null;
  private shieldRechargeAccum = 0;

  constructor(initialState: SpatialState, shipName?: string, shipFaction?: string) {
    this.spatialState = structuredClone(initialState);
    if (shipName) this.playerShipName = shipName;
    if (shipFaction) this.playerShipFaction = shipFaction;
  }

  get effects(): ActiveEffect[] {
    return this._effects;
  }

  get currentSpatialState(): SpatialState {
    return this.spatialState;
  }

  setAlertLevelCallback(cb: (level: AlertLevel) => void) {
    this.alertLevelCallback = cb;
  }

  setOnStopCallback(cb: (entityId: string) => void) {
    this.onStopCallback = cb;
  }

  setOnPlayerHitCallback(cb: ((entityId: string) => void) | null) {
    this.onPlayerHitCallback = cb;
  }

  // Notify on every incoming weapon. The React side dedups via a timestamp ref
  // and a suppression window — multiple notifications during a salvo are
  // harmless because dismissing an already-space viewscreen is a no-op.
  private notifyPlayerHit(entityId: string) {
    if (entityId !== "player_ship") return;
    this.onPlayerHitCallback?.(entityId);
  }

  setLogger(logger: MissionLogger | null) {
    this._logger = logger;
  }

  setEncounterState(state: EncounterState | null) {
    this._encounterState = state;
  }

  get encounterState(): EncounterState | null {
    return this._encounterState;
  }

  enqueue(actions: SceneAction[]) {
    console.log(`[SceneCmd] Enqueueing ${actions.length} actions:`, actions.map(a => `${a.action}(delay:${a.delay}s)`).join(", "));
    const baseTime = this.elapsed;
    for (const action of actions) {
      this.queue.push({ command: action, fireAt: baseTime + action.delay });
    }
    this.queue.sort((a, b) => a.fireAt - b.fireAt);
  }

  tick(deltaSeconds: number) {
    this.elapsed += deltaSeconds;

    // Fire queued commands
    while (this.queue.length > 0 && this.queue[0].fireAt <= this.elapsed) {
      const { command } = this.queue.shift()!;
      this.executeCommand(command);
    }

    // Auto-remove expired effects
    this._effects = this._effects.filter(
      (e) => this.elapsed - e.startedAt < e.duration
    );

    // Update active movements
    for (let i = this.movements.length - 1; i >= 0; i--) {
      const m = this.movements[i];

      if (m.kind === "move_to") {
        m.elapsed += deltaSeconds;
        const t = Math.min(m.elapsed / m.duration, 1);
        const smooth = t * t * (3 - 2 * t);
        const pos: Vec3 = {
          x: m.from.x + (m.to.x - m.from.x) * smooth,
          y: m.from.y + (m.to.y - m.from.y) * smooth,
          z: m.from.z + (m.to.z - m.from.z) * smooth,
        };
        this.setEntityPosition(m.entityId, pos);

        // Auto-rotate heading toward movement direction during move_to
        if (m.entityId === "player_ship" && m.headingFrom && m.headingTo) {
          const turnT = Math.min(m.elapsed / 5, 1); // turn completes over 5 seconds
          const turnSmooth = turnT * turnT * (3 - 2 * turnT);
          this.spatialState.playerShip.heading = lerpVec3(m.headingFrom, m.headingTo, turnSmooth);
        }

        if (t >= 1) {
          this.movements.splice(i, 1);
          if (m.orbitOnArrival) {
            // Transition into orbit around the target
            this.startOrbit(m.entityId, m.orbitOnArrival);
          } else if (m.entityId === "player_ship") {
            this.spatialState.playerShip.speed = "all_stop";
          }
        }
      } else if (m.kind === "rotate") {
        m.elapsed += deltaSeconds;
        const t = Math.min(m.elapsed / m.duration, 1);
        const smooth = t * t * (3 - 2 * t);
        if (m.entityId === "player_ship") {
          this.spatialState.playerShip.heading = lerpVec3(m.from, m.to, smooth);
        }
        if (t >= 1) {
          this.movements.splice(i, 1);
        }
      } else if (m.kind === "orbit") {
        m.elapsed += deltaSeconds;
        m.angle += m.speed * deltaSeconds;
        const target = this.getEntityPosition(m.targetId);
        if (target) {
          const pos: Vec3 = {
            x: target.x + Math.cos(m.angle) * m.radius,
            y: target.y + 2,
            z: target.z + Math.sin(m.angle) * m.radius,
          };
          this.setEntityPosition(m.entityId, pos);
          // Smoothly blend heading from approach direction to orbit tangent
          if (m.entityId === "player_ship") {
            const tangent: Vec3 = {
              x: -Math.sin(m.angle),
              y: 0,
              z: Math.cos(m.angle),
            };
            if (m.headingFrom && m.elapsed < 5) {
              const t = Math.min(m.elapsed / 5, 1);
              const smooth = t * t * (3 - 2 * t);
              this.spatialState.playerShip.heading = lerpVec3(m.headingFrom, tangent, smooth);
            } else {
              this.spatialState.playerShip.heading = tangent;
            }
          }
        }
      }
    }

    // Shield recharge: +1% per 2 seconds when shields are up
    if (this._encounterState && this.spatialState.playerShip.shields === "up") {
      const str = this._encounterState.playerShip.shieldStrength;
      if (str < 100) {
        this.shieldRechargeAccum += deltaSeconds;
        if (this.shieldRechargeAccum >= 2) {
          this.shieldRechargeAccum -= 2;
          this._encounterState.playerShip.shieldStrength = Math.min(100, str + 1);
        }
      }
    } else {
      this.shieldRechargeAccum = 0;
    }
  }

  removeEffect(id: string) {
    this._effects = this._effects.filter((e) => e.id !== id);
  }

  private addEffect(effect: Omit<ActiveEffect, "startedAt">) {
    const full = { ...effect, startedAt: this.elapsed };
    this._effects.push(full);
    this._logger?.logEffect(full);
  }

  // ── Helpers to read/write positions consistently ──

  private getEntityPosition(id: string): Vec3 | undefined {
    if (id === "player_ship") return this.spatialState.playerShip.position;
    return this.spatialState.entities.find((e) => e.id === id)?.position;
  }

  private setEntityPosition(id: string, pos: Vec3) {
    if (id === "player_ship") {
      this.spatialState.playerShip.position = pos;
    } else {
      const entity = this.spatialState.entities.find((e) => e.id === id);
      if (entity) entity.position = pos;
    }
  }

  private findEntity(id: string): SpatialEntity | undefined {
    if (id === "player_ship") {
      return {
        id: "player_ship",
        name: this.playerShipName,
        type: "ship",
        position: { ...this.spatialState.playerShip.position },
        faction: this.playerShipFaction,
        shields: this.spatialState.playerShip.shields,
      };
    }
    return this.spatialState.entities.find((e) => e.id === id);
  }

  // ── Stop and commit position ──

  // internal=true when called as part of starting a new movement (move_to, orbit).
  // Only external (explicit) stops should fire onStopCallback → cancelAllOrders.
  private stopEntity(entityId: string, internal = false) {
    // Commit current interpolated position for any active move_to
    for (const m of this.movements) {
      if (m.entityId !== entityId) continue;
      if (m.kind === "move_to") {
        const t = Math.min(m.elapsed / m.duration, 1);
        const smooth = t * t * (3 - 2 * t);
        const pos: Vec3 = {
          x: m.from.x + (m.to.x - m.from.x) * smooth,
          y: m.from.y + (m.to.y - m.from.y) * smooth,
          z: m.from.z + (m.to.z - m.from.z) * smooth,
        };
        this.setEntityPosition(entityId, pos);
        console.log(`[SceneCmd] stop: ${entityId} committed at (${pos.x.toFixed(1)},${pos.y.toFixed(1)},${pos.z.toFixed(1)})`);
      }
    }
    this.movements = this.movements.filter((m) => m.entityId !== entityId);
    if (entityId === "player_ship") {
      this.spatialState.playerShip.speed = "all_stop";
      this.spatialState.playerShip.orbitTarget = undefined;
    }
    if (!internal) {
      this.onStopCallback?.(entityId);
    }
  }

  private startOrbit(entityId: string, targetId: string) {
    const pos = this.getEntityPosition(entityId);
    const targetPos = this.getEntityPosition(targetId);
    if (!pos || !targetPos) { console.warn(`[SceneCmd] startOrbit: entity not found`); return; }
    const dx = pos.x - targetPos.x;
    const dz = pos.z - targetPos.z;
    const rawRadius = Math.sqrt(dx * dx + dz * dz) || 30;
    // Enforce minimum orbit radius based on actual planet size
    const targetEntity = this.spatialState.entities.find(e => e.id === targetId);
    const planetR = targetEntity?.planetVisuals?.radius ?? 40;
    const minRadius = targetEntity?.type === "planet" ? planetR * 1.625 : 20;
    const radius = Math.max(minRadius, rawRadius);
    const angle = Math.atan2(dz, dx);
    console.log(`[SceneCmd] startOrbit: ${entityId} orbiting ${targetId} at radius ${radius.toFixed(1)}`);
    const headingFrom = entityId === "player_ship" ? { ...this.spatialState.playerShip.heading } : undefined;
    this.movements.push({ kind: "orbit", entityId, targetId, radius, angle, speed: SCENE.ORBIT_SPEED, elapsed: 0, headingFrom });
    if (entityId === "player_ship") {
      this.spatialState.playerShip.speed = "orbit";
      this.spatialState.playerShip.orbitTarget = targetId;
    }
  }

  // ── Command execution ──

  private executeCommand(cmd: SceneAction) {
    const p = cmd.params;
    console.log(`[SceneCmd] EXEC: ${cmd.action}`, JSON.stringify(p));
    this._logger?.logSceneCommand(cmd.action, p);

    switch (cmd.action) {
      // ── MOVEMENT ──

      case "move_to": {
        // Stop first (internal), THEN read position — ensures we get the committed mid-LERP value
        this.stopEntity(p.entityId, true);
        const pos = this.getEntityPosition(p.entityId);
        if (!pos) { console.warn(`[SceneCmd] Entity not found: ${p.entityId}`); break; }
        const from = { ...pos };
        const dest = this.resolveDestination(p.destination, p.entityId);
        // Use pre-computed duration from IntentResolver if available,
        // otherwise fall back to calculating from speed and distance.
        let duration: number;
        if (p.duration && p.duration > 0) {
          duration = p.duration;
        } else {
          const dx0 = dest.x - from.x;
          const dy0 = dest.y - from.y;
          const dz0 = dest.z - from.z;
          const dist = Math.sqrt(dx0 * dx0 + dy0 * dy0 + dz0 * dz0);
          const speed = SPEED_VALUES[p.speed] || SPEED_VALUES.impulse_slow;
          duration = Math.max(2, dist / speed);
        }
        console.log(`[SceneCmd] move_to: ${p.entityId} from (${from.x.toFixed(1)},${from.y.toFixed(1)},${from.z.toFixed(1)}) → (${dest.x.toFixed(1)},${dest.y.toFixed(1)},${dest.z.toFixed(1)}) over ${duration}s`);
        // Compute heading auto-rotate for player ship
        let headingFrom: Vec3 | undefined;
        let headingTo: Vec3 | undefined;
        if (p.entityId === "player_ship") {
          headingFrom = { ...this.spatialState.playerShip.heading };
          const dx = dest.x - from.x;
          const dy = dest.y - from.y;
          const dz = dest.z - from.z;
          const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
          headingTo = { x: dx / len, y: dy / len, z: dz / len };
          // Cancel any standalone rotate since move_to handles heading
          this.movements = this.movements.filter(
            (m) => !(m.kind === "rotate" && m.entityId === p.entityId)
          );
        }
        this.movements.push({
          kind: "move_to",
          entityId: p.entityId,
          from,
          to: dest,
          duration,
          elapsed: 0,
          headingFrom,
          headingTo,
          orbitOnArrival: p.orbitOnArrival,
        });
        if (p.entityId === "player_ship" && p.speed) {
          this.spatialState.playerShip.speed = p.speed;
          soundSystem.setEngineHum(SPEED_VALUES[p.speed] ?? 2.5);
        }
        break;
      }

      case "orbit": {
        this.stopEntity(p.entityId, true);
        this.startOrbit(p.entityId, p.targetId);
        break;
      }

      case "all_stop": {
        this.stopEntity(p.entityId);
        if (p.entityId === "player_ship") {
          this.spatialState.playerShip.orbitTarget = undefined;
          soundSystem.setEngineHum(0);
        }
        break;
      }

      case "rotate": {
        const pos = this.getEntityPosition(p.entityId);
        if (!pos) { console.warn(`[SceneCmd] rotate: entity not found: ${p.entityId}`); break; }
        // Resolve target heading direction
        let targetHeading: Vec3 | null = null;
        const target = this.findEntity(p.heading);
        if (target) {
          const dx = target.position.x - pos.x;
          const dy = target.position.y - pos.y;
          const dz = target.position.z - pos.z;
          const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
          targetHeading = { x: dx / len, y: dy / len, z: dz / len };
        }
        if (targetHeading && p.entityId === "player_ship") {
          // Cancel any existing rotate for this entity
          this.movements = this.movements.filter(
            (m) => !(m.kind === "rotate" && m.entityId === p.entityId)
          );
          const from = { ...this.spatialState.playerShip.heading };
          const duration = p.durationSeconds || 12;
          this.movements.push({
            kind: "rotate",
            entityId: p.entityId,
            from,
            to: targetHeading,
            duration,
            elapsed: 0,
          });
          console.log(`[SceneCmd] rotate: ${p.entityId} turning to face ${p.heading} over ${duration}s`);
        }
        break;
      }

      // ── WEAPONS ──

      case "fire_beam": {
        const sourcePos = this.getEntityPosition(p.sourceId);
        const targetPos = this.getEntityPosition(p.targetId);
        if (!sourcePos || !targetPos) { console.warn(`[SceneCmd] fire_beam: entity not found`); break; }
        const color = WEAPON_COLORS[p.weaponType] || "#4488ff";
        const duration = p.durationSeconds || cmd.duration || 2;
        const beamHit = p.hit !== false;
        soundSystem.phaser();
        console.log(`[SceneCmd] fire_beam: ${p.sourceId} → ${p.targetId} (${p.weaponType}, ${duration}s, hit=${beamHit})`);
        // If we're being shot at, force viewscreen back to space so the player can see it.
        if (p.targetId === "player_ship") this.notifyPlayerHit("player_ship");
        // For misses/warning shots, offset the beam endpoint past the target
        let beamTarget = { ...targetPos };
        if (!beamHit) {
          const dx = targetPos.x - sourcePos.x;
          const dz = targetPos.z - sourcePos.z;
          beamTarget = {
            x: targetPos.x + dx * 0.4 + (Math.random() - 0.5) * 20,
            y: targetPos.y + (Math.random() - 0.5) * 10,
            z: targetPos.z + dz * 0.4 + (Math.random() - 0.5) * 20,
          };
        }
        this.addEffect({
          id: `effect_${this.effectIdCounter++}`,
          type: "beam",
          from: { ...sourcePos },
          to: beamTarget,
          color,
          duration,
        });
        // If hit, add shield impact or hull damage after a short delay
        if (p.hit !== false) {
          setTimeout(() => {
            this.addEffect({
              id: `effect_${this.effectIdCounter++}`,
              type: "shield_impact",
              position: { ...this.getEntityPosition(p.targetId)! },
              color: this.findEntity(p.targetId)?.shields === "up" ? color : "#ff8800",
              duration: 0.6,
            });
          }, duration * 500);
        }
        break;
      }

      case "fire_torpedo": {
        const sourcePos = this.getEntityPosition(p.sourceId);
        const targetPos = this.getEntityPosition(p.targetId);
        if (!sourcePos || !targetPos) { console.warn(`[SceneCmd] fire_torpedo: entity not found`); break; }
        const color = WEAPON_COLORS[p.torpedoType] || "#ff6600";
        const count = p.count || 1;
        const hit = p.hit !== false;
        soundSystem.torpedo();
        console.log(`[SceneCmd] fire_torpedo: ${p.sourceId} → ${p.targetId} (${p.torpedoType} x${count}, hit=${hit})`);
        if (p.targetId === "player_ship") this.notifyPlayerHit("player_ship");

        // For misses/warning shots, compute a point past the target
        let destination = { ...targetPos };
        if (!hit) {
          const dx = targetPos.x - sourcePos.x;
          const dy = targetPos.y - sourcePos.y;
          const dz = targetPos.z - sourcePos.z;
          // Fly 1.5x past the target with a lateral offset so it visually "crosses the bow"
          destination = {
            x: targetPos.x + dx * 0.5 + (Math.random() - 0.5) * 30,
            y: targetPos.y + dy * 0.5 + (Math.random() - 0.5) * 10,
            z: targetPos.z + dz * 0.5 + (Math.random() - 0.5) * 30,
          };
        }

        for (let i = 0; i < count; i++) {
          // Stagger torpedoes slightly
          const offset = i * 0.3;
          this.addEffect({
            id: `effect_${this.effectIdCounter++}`,
            type: "torpedo",
            from: {
              x: sourcePos.x + (Math.random() - 0.5) * 2,
              y: sourcePos.y + (Math.random() - 0.5) * 1,
              z: sourcePos.z + (Math.random() - 0.5) * 2,
            },
            to: destination,
            color,
            duration: hit ? 3 + offset : 4 + offset,
          });
        }
        break;
      }

      // ── DEFENSES ──

      case "raise_shields": {
        if (p.entityId === "player_ship") {
          this.spatialState.playerShip.shields = "up";
          soundSystem.shieldsUp();
        }
        const entity = this.spatialState.entities.find((e) => e.id === p.entityId);
        if (entity) entity.shields = "up";
        console.log(`[SceneCmd] raise_shields: ${p.entityId}`);
        break;
      }

      case "lower_shields": {
        if (p.entityId === "player_ship") {
          this.spatialState.playerShip.shields = "down";
          soundSystem.shieldsDown();
        }
        const entity = this.spatialState.entities.find((e) => e.id === p.entityId);
        if (entity) entity.shields = "down";
        console.log(`[SceneCmd] lower_shields: ${p.entityId}`);
        break;
      }

      case "shield_impact": {
        soundSystem.shieldImpact();
        const pos = this.getEntityPosition(p.entityId);
        if (!pos) break;
        const entity = this.findEntity(p.entityId);
        this.addEffect({
          id: `effect_${this.effectIdCounter++}`,
          type: "shield_impact",
          position: { ...pos },
          color: entity?.faction === "Velan Dominion" ? "#ff4444" : "#4488ff",
          duration: 0.6,
        });
        break;
      }

      case "hull_damage": {
        const dmgAmount: Record<string, number> = { light: 5, moderate: 15, heavy: 30, critical: 50 };
        const rawDmg = dmgAmount[p.severity] ?? 10;
        let hullHit = true;

        // Shield absorption — shields take damage first
        if (p.entityId === "player_ship" && this._encounterState) {
          const ship = this._encounterState.playerShip;
          if (this.spatialState.playerShip.shields === "up" && ship.shieldStrength > 0) {
            ship.shieldStrength = Math.max(0, ship.shieldStrength - rawDmg);
            if (ship.shieldStrength <= 0) {
              // Shields collapsed
              this.executeCommand({ action: "lower_shields", params: { entityId: "player_ship" }, delay: 0 });
              ship.shieldStrength = 0;
            } else {
              hullHit = false; // shields absorbed it fully
            }
          }
          // Hull integrity loss (reduced if shields absorbed some)
          if (hullHit) {
            ship.hullIntegrity = Math.max(0, ship.hullIntegrity - Math.round(rawDmg * 0.7));
            ship.condition = ship.hullIntegrity > 75 ? "healthy" : ship.hullIntegrity > 50 ? "damaged" : ship.hullIntegrity > 25 ? "badly_damaged" : "critical";
          }
          // Subsystem damage
          if (p.subsystem && ship.systems[p.subsystem as keyof typeof ship.systems]) {
            const sys = ship.systems[p.subsystem as keyof typeof ship.systems];
            sys.efficiency = Math.max(0, sys.efficiency - Math.round(rawDmg * 0.8));
            sys.status = sys.efficiency >= 80 ? "operational" : sys.efficiency >= 50 ? "degraded" : sys.efficiency >= 20 ? "damaged" : "offline";
          }
        } else {
          // NPC entity damage
          const entity = this.spatialState.entities.find((e) => e.id === p.entityId);
          if (entity) {
            if (entity.shields === "up") {
              // NPC shields absorb — downgrade severity
              if (p.severity !== "critical") { hullHit = false; }
              else { entity.shields = "down"; } // critical hit breaks shields
            }
            if (hullHit) {
              entity.condition = p.severity === "critical" ? "critical" : p.severity === "heavy" ? "badly_damaged" : "damaged";
            }
          }
        }

        // Bridge shake on hull damage (stronger for player ship)
        if (p.entityId === "player_ship") {
          const shakeIntensity = p.severity === "critical" ? 2.5 : p.severity === "heavy" ? 1.5 : 0.8;
          this.executeCommand({ action: "bridge_shake", params: { intensity: shakeIntensity }, delay: 0 });
        }
        // Visual: explosion at the entity — bigger for heavier damage
        const pos = this.getEntityPosition(p.entityId);
        if (pos) {
          this.addEffect({
            id: `effect_${this.effectIdCounter++}`,
            type: "explosion",
            position: { ...pos },
            size: p.severity === "critical" ? 6 : p.severity === "heavy" ? 4 : 2,
            duration: p.severity === "critical" ? 1.5 : 1,
          });
          soundSystem.explosion(p.severity === "critical");
        }
        break;
      }

      // ── SENSORS & EFFECTS ──

      case "scan_beam": {
        const sourcePos = this.getEntityPosition(p.sourceId);
        const targetPos = this.getEntityPosition(p.targetId);
        if (!sourcePos || !targetPos) { console.warn(`[SceneCmd] scan_beam: entity not found`); break; }
        const duration = p.durationSeconds || cmd.duration || 5;
        console.log(`[SceneCmd] scan_beam: ${p.sourceId} → ${p.targetId} (${duration}s)`);
        this.addEffect({
          id: `effect_${this.effectIdCounter++}`,
          type: "scan",
          from: { ...sourcePos },
          to: { ...targetPos },
          duration,
        });
        break;
      }

      case "tractor_beam": {
        const sourcePos = this.getEntityPosition(p.sourceId);
        const targetPos = this.getEntityPosition(p.targetId);
        if (!sourcePos || !targetPos) { console.warn(`[SceneCmd] tractor_beam: entity not found`); break; }
        if (p.state === "engage") {
          console.log(`[SceneCmd] tractor_beam: engaging ${p.sourceId} → ${p.targetId}`);
          this.addEffect({
            id: `effect_tractor_${p.sourceId}_${p.targetId}`,
            type: "tractor",
            from: { ...sourcePos },
            to: { ...targetPos },
            color: "#88ccff",
            duration: 300, // Long duration, removed by disengage
          });
        } else {
          console.log(`[SceneCmd] tractor_beam: disengaging`);
          this._effects = this._effects.filter(
            (e) => e.id !== `effect_tractor_${p.sourceId}_${p.targetId}`
          );
        }
        break;
      }

      case "transporter": {
        const sourcePos = this.getEntityPosition(p.sourceId);
        const targetPos = this.getEntityPosition(p.targetId);
        if (!sourcePos || !targetPos) break;
        soundSystem.transporter();
        // Transporter shimmer at both endpoints
        for (const pos of [sourcePos, targetPos]) {
          this.addEffect({
            id: `effect_${this.effectIdCounter++}`,
            type: "transporter",
            position: { ...pos },
            duration: 3,
          });
        }
        console.log(`[SceneCmd] transporter: ${p.sourceId} → ${p.targetId}`);
        break;
      }

      case "launch": {
        const sourcePos = this.getEntityPosition(p.sourceId);
        if (!sourcePos) break;
        // Shuttles/probes go directly to the target entity position (land/dock),
        // not the clearance orbit distance used by ships
        const targetEntity = p.targetId ? this.findEntity(p.targetId) : null;
        const destPos = targetEntity
          ? { ...targetEntity.position }
          : this.resolveDestination(p.destination || p.targetId || "system_edge", p.sourceId);
        const launchId = `launched_${this.effectIdCounter++}`;
        const objType = p.objectType || "shuttle";
        const objNames: Record<string, string> = { shuttle: "Shuttle", probe: "Probe", escape_pod: "Escape Pod" };
        this.spatialState.entities.push({
          id: launchId,
          name: objNames[objType] || objType,
          type: "shuttle",
          position: { ...sourcePos },
          faction: this.playerShipFaction,
        });
        const dx = destPos.x - sourcePos.x;
        const dy = destPos.y - sourcePos.y;
        const dz = destPos.z - sourcePos.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
        const speed = objType === "probe" ? 8 : 4;
        this.movements.push({
          kind: "move_to",
          entityId: launchId,
          from: { ...sourcePos },
          to: destPos,
          duration: Math.max(5, dist / speed),
          elapsed: 0,
        });
        console.log(`[SceneCmd] launch: ${objType} from ${p.sourceId} toward ${p.targetId || p.destination}`);
        break;
      }

      case "cloak": {
        const entity = this.spatialState.entities.find(e => e.id === p.entityId);
        if (entity) {
          entity.cloaked = true;
          soundSystem.cloak();
          const pos = entity.position;
          this.addEffect({
            id: `effect_${this.effectIdCounter++}`,
            type: "cloak",
            position: { ...pos },
            size: 8,
            duration: 1.5,
          });
          console.log(`[SceneCmd] cloak: ${p.entityId}`);
        }
        break;
      }

      case "decloak": {
        const entity = this.spatialState.entities.find(e => e.id === p.entityId);
        if (entity) {
          entity.cloaked = false;
          soundSystem.decloak();
          const pos = entity.position;
          this.addEffect({
            id: `effect_${this.effectIdCounter++}`,
            type: "cloak",
            position: { ...pos },
            size: 8,
            duration: 1.5,
          });
          console.log(`[SceneCmd] decloak: ${p.entityId}`);
        }
        break;
      }

      case "evasive": {
        // Evasive maneuvers — jink pattern around current position
        this.stopEntity(p.entityId, true);
        const pos = this.getEntityPosition(p.entityId);
        if (!pos) break;
        const duration = p.durationSeconds || 8;
        // Create a series of rapid short moves to simulate dodging
        const jinkCount = 6;
        for (let i = 0; i < jinkCount; i++) {
          const angle = Math.random() * Math.PI * 2;
          const radius = 5 + Math.random() * 10;
          const jinkDest: Vec3 = {
            x: pos.x + Math.cos(angle) * radius,
            y: pos.y + (Math.random() - 0.5) * 4,
            z: pos.z + Math.sin(angle) * radius,
          };
          const jinkDelay = (duration / jinkCount) * i;
          this.queue.push({
            command: {
              action: "move_to",
              params: { entityId: p.entityId, destination: jinkDest, speed: "impulse_full" },
              delay: 0,
            },
            fireAt: this.elapsed + jinkDelay,
          });
        }
        // Return to roughly original position at the end
        this.queue.push({
          command: {
            action: "move_to",
            params: { entityId: p.entityId, destination: pos, speed: "impulse_half" },
            delay: 0,
          },
          fireAt: this.elapsed + duration,
        });
        this.queue.sort((a, b) => a.fireAt - b.fireAt);
        if (p.entityId === "player_ship") {
          soundSystem.setEngineHum(SPEED_VALUES.impulse_full);
        }
        console.log(`[SceneCmd] evasive: ${p.entityId} for ${duration}s`);
        break;
      }

      // ── ENVIRONMENT ──

      case "bridge_shake": {
        const intensity = p.intensity || 1;
        const duration = p.durationSeconds || 0.8;
        console.log(`[SceneCmd] bridge_shake: intensity=${intensity}, duration=${duration}s`);
        triggerShake(intensity, duration);
        break;
      }

      case "set_alert": {
        const validLevels: AlertLevel[] = ["normal", "yellow", "red"];
        const level = validLevels.includes(p.level as AlertLevel) ? (p.level as AlertLevel) : "normal";
        console.log(`[SceneCmd] set_alert: ${level}`);
        if (level === "red")    soundSystem.redAlert();
        else if (level === "yellow") soundSystem.yellowAlert();
        else                   soundSystem.standDown();
        this.alertLevelCallback?.(level);
        break;
      }

      case "explosion": {
        const pos = p.position ? p.position : this.getEntityPosition(p.entityId);
        if (!pos) break;
        this.addEffect({
          id: `effect_${this.effectIdCounter++}`,
          type: "explosion",
          position: { ...pos },
          size: p.size || 3,
          duration: 1.2,
        });
        break;
      }

      case "camera": {
        // Camera mode changes — handled by Viewport3D reading from spatial state
        console.log(`[SceneCmd] camera: mode=${p.mode}, target=${p.targetId || "none"}`);
        break;
      }

      // ── SPAWN / DESPAWN ──

      case "warp_in": {
        soundSystem.warp();
        triggerWarp();
        const pos = this.resolveDestination(p.direction || "system_edge", "");
        if (p.entityId === "player_ship") {
          // Player ship lives in spatialState.playerShip, not entities array
          this.spatialState.playerShip.position = { ...pos };
          this.spatialState.playerShip.speed = "all_stop";
        } else {
          const newEntity: SpatialEntity = {
            id: p.entityId,
            name: p.shipData?.name || p.entityId,
            type: "ship",
            position: pos,
            faction: p.shipData?.faction,
            shipClass: p.shipData?.shipClass,
            shields: "down",
          };
          this.spatialState.entities.push(newEntity);
        }
        this.addEffect({
          id: `effect_${this.effectIdCounter++}`,
          type: "explosion",
          position: { ...pos },
          size: 2,
          duration: 0.8,
        });
        console.log(`[SceneCmd] warp_in: ${p.entityId} at (${pos.x.toFixed(1)},${pos.y.toFixed(1)},${pos.z.toFixed(1)})`);
        break;
      }

      case "warp_out": {
        soundSystem.warp();
        triggerWarp();
        const pos = this.getEntityPosition(p.entityId);
        if (pos) {
          this.addEffect({
            id: `effect_${this.effectIdCounter++}`,
            type: "explosion",
            position: { ...pos },
            size: 2,
            duration: 0.8,
          });
        }
        this.movements = this.movements.filter((m) => m.entityId !== p.entityId);
        this.spatialState.entities = this.spatialState.entities.filter((e) => e.id !== p.entityId);
        console.log(`[SceneCmd] warp_out: ${p.entityId}`);
        break;
      }

      case "ship_destroyed": {
        soundSystem.explosion(true);
        const pos = this.getEntityPosition(p.entityId);
        if (pos) {
          // Primary explosion — large
          this.addEffect({
            id: `effect_${this.effectIdCounter++}`,
            type: "explosion",
            position: { ...pos },
            size: 10,
            duration: 2.5,
          });
          // Secondary explosions — staggered smaller blasts
          for (let i = 0; i < 3; i++) {
            setTimeout(() => {
              this.addEffect({
                id: `effect_${this.effectIdCounter++}`,
                type: "explosion",
                position: {
                  x: pos.x + (Math.random() - 0.5) * 12,
                  y: pos.y + (Math.random() - 0.5) * 6,
                  z: pos.z + (Math.random() - 0.5) * 12,
                },
                size: 4 + Math.random() * 3,
                duration: 1.5,
              });
              soundSystem.explosion(false);
            }, 300 + i * 400);
          }
        }
        // Convert to debris after explosions settle — stop movement, make it a wreck
        setTimeout(() => {
          this.movements = this.movements.filter((m) => m.entityId !== p.entityId);
          const ent = this.spatialState.entities.find((e) => e.id === p.entityId);
          if (ent) {
            ent.type = "debris";
            ent.condition = "destroyed";
            ent.shields = "down";
            ent.name = `${ent.name} (Wreck)`;
          }
        }, 500);
        console.log(`[SceneCmd] ship_destroyed → debris: ${p.entityId}`);
        break;
      }

      default:
        console.warn(`[SceneCmd] Unknown command: ${cmd.action}`);
    }
  }

  // ── Destination resolver ──

  private resolveDestination(dest: string, entityId: string): Vec3 {
    if (typeof dest === "object" && "x" in dest) return dest as Vec3;

    const player = this.spatialState.playerShip.position;
    const planet = this.spatialState.entities.find((e) => e.type === "planet");

    switch (dest) {
      case "player_intercept":
        return { x: player.x + 10, y: player.y, z: player.z };

      case "orbit_planet":
      case "orbit_telos_iv":
        if (planet) {
          const pR = planet.planetVisuals?.radius ?? 40;
          const clearance = pR * 1.75;
          return {
            x: planet.position.x + clearance,
            y: planet.position.y + 5,
            z: planet.position.z,
          };
        }
        return player;

      case "position_between_player_and_planet":
        if (planet) {
          return {
            x: (player.x + planet.position.x) / 2,
            y: (player.y + planet.position.y) / 2,
            z: (player.z + planet.position.z) / 2,
          };
        }
        return player;

      case "system_edge":
        return { x: -80, y: 0, z: 0 };

      default: {
        // Check if destination is an entity ID
        const targetEntity = this.findEntity(dest);
        if (targetEntity) {
          const dx = targetEntity.position.x - player.x;
          const dy = targetEntity.position.y - player.y;
          const dz = targetEntity.position.z - player.z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
          // Respect entity type — planets need more clearance based on actual radius
          const pRadius = (targetEntity as any).planetVisuals?.radius ?? 40;
          const minStop = targetEntity.type === "planet" ? pRadius * 1.75 : 20;
          const stopDist = Math.max(minStop, dist * 0.3);
          const ratio = Math.max(0, (dist - stopDist) / dist);
          return {
            x: player.x + dx * ratio,
            y: player.y + dy * ratio,
            z: player.z + dz * ratio,
          };
        }
        console.warn(`[SceneCmd] Unknown destination: "${dest}"`);
        return this.getEntityPosition(entityId) ?? player;
      }
    }
  }

  /** Merge server state for NPC entities only.
   *  The executor is authoritative for all positions — server state
   *  is stale by the time the response arrives due to real-time animation. */
  updateSpatialState(state: SpatialState) {
    const movingIds = new Set(this.movements.map((m) => m.entityId));

    for (const serverEntity of state.entities) {
      if (movingIds.has(serverEntity.id)) continue;
      const local = this.spatialState.entities.find((e) => e.id === serverEntity.id);
      if (local) {
        Object.assign(local, serverEntity);
      } else {
        this.spatialState.entities.push(structuredClone(serverEntity));
      }
    }
    // Never overwrite player ship — executor owns its position and heading
  }
}
